/**
 * CRMPoliciesPage — Lista polizze emesse (CRM-2, feb 2026).
 * Tabella paginata 50/pag, filtri sticky, drawer dettaglio, bottone Nuova polizza.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Search, Plus, FileText, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const RAMI = ["auto", "casa", "vita", "salute", "cat_nat", "aziende", "commercio",
               "ris", "fondi_pensione", "infortuni", "rc_professionale", "altro"];
const STATI = ["attiva", "sospesa", "annullata", "scaduta", "in_emissione", "rinnovata"];

const STATO_COLOR = {
  attiva: "bg-emerald-100 text-emerald-700 border-emerald-200",
  sospesa: "bg-amber-100 text-amber-700 border-amber-200",
  annullata: "bg-zinc-100 text-zinc-500 border-zinc-200",
  scaduta: "bg-rose-100 text-rose-700 border-rose-200",
  in_emissione: "bg-sky-100 text-sky-700 border-sky-200",
  rinnovata: "bg-violet-100 text-violet-700 border-violet-200",
};

export default function CRMPoliciesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [ramo, setRamo] = useState("");
  const [stato, setStato] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", "50");
      if (q.trim()) params.set("q", q.trim());
      if (ramo) params.set("ramo", ramo);
      if (stato) params.set("stato", stato);
      const r = await axios.get(`${API}/api/admin/crm/policies?${params.toString()}`, { withCredentials: true });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [page, q, ramo, stato]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6" data-testid="crm-policies-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Polizze ({total})</h2>
          <p className="text-xs text-zinc-500">Storico emesso con scadenze, premio, compagnia · soft delete per compliance</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="crm-policy-new-btn"
          className="px-3 py-2 text-sm bg-zinc-900 text-white rounded hover:bg-zinc-800 flex items-center gap-1">
          <Plus className="w-4 h-4" /> Nuova polizza
        </button>
        <a href={`${API}/api/admin/crm/export/policies?format=xlsx`} target="_blank" rel="noreferrer"
           className="px-3 py-2 text-sm bg-emerald-100 text-emerald-700 rounded flex items-center gap-1" data-testid="crm-policies-export-btn">
          <FileText className="w-4 h-4" /> Export
        </a>
      </div>

      <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 mb-3 flex flex-wrap gap-2 pb-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-zinc-400" />
          <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
            placeholder="N° polizza, compagnia..."
            className="w-full pl-8 pr-3 py-2 text-sm rounded border border-zinc-200" data-testid="crm-policies-search" />
        </div>
        <select value={ramo} onChange={(e) => { setPage(1); setRamo(e.target.value); }}
          className="px-2 py-2 text-sm rounded border border-zinc-200" data-testid="crm-policies-filter-ramo">
          <option value="">Tutti i rami</option>
          {RAMI.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={stato} onChange={(e) => { setPage(1); setStato(e.target.value); }}
          className="px-2 py-2 text-sm rounded border border-zinc-200" data-testid="crm-policies-filter-stato">
          <option value="">Tutti gli stati</option>
          {STATI.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="text-center py-8 text-zinc-400 italic">Nessuna polizza</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs md:text-sm" data-testid="crm-policies-table">
            <thead className="border-b border-zinc-200 text-left text-zinc-500">
              <tr>
                <th className="py-2 pr-2">N°</th><th>Cliente</th><th>Compagnia</th><th>Ramo</th>
                <th>Scadenza</th><th>Premio</th><th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} onClick={() => setSelectedId(p.id)}
                    className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                    data-testid={`crm-policy-row-${p.id}`}>
                  <td className="py-2 pr-2 font-mono">{p.numero_polizza}</td>
                  <td>{p.customer_label}</td>
                  <td>{p.compagnia}</td>
                  <td>{p.ramo}</td>
                  <td>{p.scadenza?.slice(0, 10)}</td>
                  <td>€ {Number(p.premio_lordo_eur || 0).toFixed(2)}</td>
                  <td><span className={`px-1.5 py-0.5 rounded border text-[10px] ${STATO_COLOR[p.stato] || ""}`}>{p.stato}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && <PolicyDrawer id={selectedId} onClose={() => { setSelectedId(null); load(); }} />}
      {showCreate && <PolicyCreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function PolicyDrawer({ id, onClose }) {
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/api/admin/crm/policies/${id}`, { withCredentials: true }).then((r) => setData(r.data.policy));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const softDelete = async () => {
    if (!confirming) { setConfirming(true); return; }
    await axios.delete(`${API}/api/admin/crm/policies/${id}`, { withCredentials: true });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-end" onClick={onClose} data-testid="crm-policy-drawer">
      <div className="bg-white w-full md:max-w-lg md:h-full overflow-y-auto rounded-t-2xl md:rounded-t-none md:rounded-l-2xl p-5" onClick={(e) => e.stopPropagation()}>
        {!data ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            <h3 className="text-lg font-semibold mb-1 font-mono">{data.numero_polizza}</h3>
            <p className="text-xs text-zinc-500 mb-3">{data.compagnia} · {data.ramo} · {data.stato}</p>
            <div className="space-y-1 text-sm">
              <p><strong>Decorrenza:</strong> {data.decorrenza?.slice(0, 10)}</p>
              <p><strong>Scadenza:</strong> {data.scadenza?.slice(0, 10)}</p>
              <p><strong>Premio lordo:</strong> € {Number(data.premio_lordo_eur || 0).toFixed(2)}</p>
              <p><strong>Frazionamento:</strong> {data.frazionamento}</p>
              <p><strong>Tipo emissione:</strong> {data.tipo_emissione}</p>
              {data.tags?.length > 0 && <p><strong>Tag:</strong> {data.tags.join(", ")}</p>}
            </div>
            {data.note?.length > 0 && (
              <div className="mt-3 border-t border-zinc-100 pt-2">
                <p className="text-xs text-zinc-500 mb-1">Note ({data.note.length})</p>
                {data.note.map((n, i) => (
                  <div key={i} className="text-xs border border-zinc-100 rounded p-1.5 mb-1">
                    <p className="text-zinc-500">{n.ts?.slice(0, 16).replace("T", " ")} · {n.user_id}</p>
                    <p>{n.content}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button onClick={onClose} className="flex-1 py-2 text-sm rounded bg-zinc-100 hover:bg-zinc-200">Chiudi</button>
              {data.stato !== "annullata" && (
                <button onClick={softDelete} data-testid="crm-policy-delete-btn"
                  className="px-3 py-2 text-sm rounded bg-rose-50 text-rose-700 hover:bg-rose-100 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> {confirming ? "Conferma" : "Annulla polizza"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PolicyCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    ramo: "auto", frazionamento: "annuale", tipo_emissione: "nuova",
    compagnia_id: "", compagnia: "",
  });
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [error, setError] = useState(null);
  const [compagnie, setCompagnie] = useState([]);

  // Carica compagnie partner attive (per dropdown)
  useEffect(() => {
    axios.get(`${API}/api/admin/crm/compagnie-partner?mandato_attivo=true`,
              { withCredentials: true })
      .then((r) => setCompagnie(r.data.items || []))
      .catch(() => setCompagnie([]));
  }, []);

  // Autocomplete customer
  useEffect(() => {
    if (custQuery.length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/admin/crm/customers/search?q=${encodeURIComponent(custQuery)}`, { withCredentials: true })
        .then((r) => setCustResults(r.data.items || []));
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  const submit = async () => {
    setError(null);
    if (!selectedCust) { setError("Seleziona un cliente"); return; }
    try {
      await axios.post(`${API}/api/admin/crm/policies`, {
        ...form,
        customer_id: selectedCust.id,
        premio_lordo_eur: parseFloat(form.premio_lordo_eur || 0),
      }, { withCredentials: true });
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail?.error_code || e.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-policy-create-modal">
        <h3 className="text-lg font-semibold mb-3">Nuova polizza</h3>
        <div className="space-y-2">
          <div className="relative">
            <input value={custQuery} onChange={(e) => { setCustQuery(e.target.value); setSelectedCust(null); }}
              placeholder="Cerca cliente (nome, email)..."
              className="w-full px-3 py-2 text-sm rounded border border-zinc-200" data-testid="crm-policy-cust-search" />
            {custResults.length > 0 && !selectedCust && (
              <div className="absolute z-10 bg-white border border-zinc-200 rounded shadow w-full max-h-40 overflow-y-auto">
                {custResults.map((c) => (
                  <button key={c.id} onClick={() => { setSelectedCust(c); setCustQuery(c.anagrafica?.ragione_sociale || `${c.anagrafica?.nome} ${c.anagrafica?.cognome}`); setCustResults([]); }}
                    className="block w-full text-left px-2 py-1 text-xs hover:bg-zinc-50">
                    {c.anagrafica?.ragione_sociale || `${c.anagrafica?.nome || ""} ${c.anagrafica?.cognome || ""}`}
                    <span className="text-zinc-400 ml-2">{c.anagrafica?.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedCust && <p className="text-xs text-emerald-700">✓ {selectedCust.anagrafica?.email}</p>}
          <div>
            <label className="text-xs font-medium text-zinc-600">Compagnia</label>
            <select
              value={form.compagnia_id || ""}
              onChange={(e) => {
                const id = e.target.value;
                const found = compagnie.find((c) => c.id === id);
                setForm({
                  ...form, compagnia_id: id,
                  compagnia: found
                    ? (found.nome_commerciale || found.ragione_sociale)
                    : "",
                });
              }}
              className="w-full px-3 py-2 text-sm rounded border border-zinc-200"
              data-testid="crm-policy-compagnia-select"
            >
              <option value="">— Seleziona compagnia —</option>
              {compagnie.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome_commerciale || c.ragione_sociale}
                  {c.tipo === "collaborazione_orizzontale" ? " 🤝" : ""}
                </option>
              ))}
            </select>
            <a href="/admin#crm_compagnie_partner" target="_blank"
                rel="noreferrer"
                className="text-[10px] text-zinc-500 underline hover:text-zinc-700">
              + Aggiungi compagnia mancante
            </a>
          </div>
          <select value={form.ramo} onChange={(e) => setForm({ ...form, ramo: e.target.value })}
            className="w-full px-3 py-2 text-sm rounded border border-zinc-200">
            {RAMI.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <input placeholder="Numero polizza" onChange={(e) => setForm({ ...form, numero_polizza: e.target.value })}
            className="w-full px-3 py-2 text-sm rounded border border-zinc-200 font-mono" />
          <div className="grid grid-cols-2 gap-2">
            <input type="date" placeholder="Decorrenza" onChange={(e) => setForm({ ...form, decorrenza: e.target.value })}
              className="px-3 py-2 text-sm rounded border border-zinc-200" />
            <input type="date" placeholder="Scadenza" onChange={(e) => setForm({ ...form, scadenza: e.target.value })}
              className="px-3 py-2 text-sm rounded border border-zinc-200" />
          </div>
          <input type="number" step="0.01" placeholder="Premio lordo €" onChange={(e) => setForm({ ...form, premio_lordo_eur: e.target.value })}
            className="w-full px-3 py-2 text-sm rounded border border-zinc-200" />
          <select value={form.frazionamento} onChange={(e) => setForm({ ...form, frazionamento: e.target.value })}
            className="w-full px-3 py-2 text-sm rounded border border-zinc-200">
            <option value="annuale">Annuale</option>
            <option value="semestrale">Semestrale</option>
            <option value="trimestrale">Trimestrale</option>
            <option value="mensile">Mensile</option>
            <option value="unico">Premio unico</option>
          </select>
        </div>
        {error && <p className="text-xs text-rose-600 mt-2">{String(error)}</p>}
        <div className="flex gap-2 mt-3">
          <button onClick={onClose} className="flex-1 py-2 text-sm rounded bg-zinc-100">Annulla</button>
          <button onClick={submit} data-testid="crm-policy-create-submit"
            className="flex-1 py-2 text-sm rounded bg-zinc-900 text-white">Crea polizza</button>
        </div>
      </div>
    </div>
  );
}
